// Fase 17.3, Plano 06 (D-25) — "Cadastrar peça": cria ou reaproveita a peça, o vínculo do cliente e o roteiro, e liga o item do PV, tudo em uma requisição.
// Dois jeitos de chegar aqui: pelo item do PV (?pedidoVendaId=&itemId=) ou pelo código do cliente (?clienteId=&codigo=[&pendenciaId=]), que é o caso
// da remessa manual recusada e da pendência de produto sem cadastro. Sem parâmetros a tela mostra a lista de "Produto sem cadastro".
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { MaterialLookupPv, PedidoVendaItemView, PedidoVendaView } from '@pieltec/contracts';
import { MaterialCombobox } from '../../../components/vendas/material-combobox';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { Textarea } from '../../../components/ui/textarea';
import { apiFetch } from '../../../lib/csrf-fetch';
import {
  cadastrarPecaDoCodigo,
  cadastrarPecaDoItem,
  corpoCadastrarPecaDoCodigo,
  corpoCadastrarPecaDoItem,
  faltaPermissaoDeVinculo,
  linkDaFichaDaPeca,
  materialParaEnviar,
  MENSAGEM_PECA_CADASTRADA,
  MENSAGEM_PECA_CADASTRADA_PELO_CODIGO,
  MENSAGEM_PENDENCIA_RESOLVIDA,
  MENSAGEM_SEM_PERMISSAO_MATERIAL_NOVO,
  MENSAGEM_SEM_PERMISSAO_VINCULAR,
  modoDaNovaPeca,
  NOTA_MATERIAL_DA_OMIE,
  parametrosDaNovaPeca,
  PERMISSAO_CADASTRAR,
  PERMISSAO_CRIAR_MATERIAL,
  validarFormularioMaterial,
  valoresIniciaisDoItem,
  type FormularioMaterial,
  type ParametrosNovaPeca,
} from '../../../lib/roteiros/cadastro-peca';
import { etapasParaSalvar, validarEtapas, type EtapaEdicao } from '../../../lib/roteiros/etapas';
import { servicosComCelulas } from '../../../lib/roteiros/roteiros-api';
import { obterSessaoAtual } from '../../../lib/session';
import { EditorEtapas } from './editor-etapas';
import { IniciarRoteiroDialog, type InicioDeRoteiro } from './iniciar-roteiro-dialog';
import { ListaProdutosSemCadastro } from './produtos-sem-cadastro';

const MOTIVO_MINIMO = 5;
const MOTIVO_PADRAO = 'Cadastro inicial da peça';
const ATRASO_NAVEGACAO_MS = 1500;

function usePermissoes(): readonly string[] {
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => { if (vivo && sessao.autenticado) setPermissoes(sessao.usuario.permissions); });
    return () => { vivo = false; };
  }, []);
  return permissoes;
}

function Pagina({ children }: { readonly children: ReactNode }) {
  return <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">{children}</div>;
}

export function NovaPecaPage() {
  const consulta = window.location.search;
  const modo = modoDaNovaPeca(consulta);
  const parametros = useMemo(() => parametrosDaNovaPeca(consulta), [consulta]);
  const permissoes = usePermissoes();
  const podeCadastrar = permissoes.includes(PERMISSAO_CADASTRAR) && permissoes.includes('pv:editar');

  if (modo === 'LISTA') {
    return (
      <Pagina>
        <Card>
          <CardContent className="flex flex-col gap-1">
            <a className="text-sm text-muted-foreground underline-offset-4 hover:underline" href="/pcp/roteiros">Roteiros das peças</a>
            <h1 className="text-[28px] font-semibold leading-tight">Produto sem cadastro</h1>
            <p className="text-sm text-muted-foreground">Itens de pedido sem peça cadastrada. Enquanto não houver peça e roteiro, a OP não é gerada.</p>
          </CardContent>
        </Card>
        <ListaProdutosSemCadastro podeCadastrar={podeCadastrar} />
      </Pagina>
    );
  }
  return <FormularioCadastro modo={modo} parametros={parametros} permissoes={permissoes} />;
}

function FormularioCadastro({ modo, parametros, permissoes }: { readonly modo: 'ITEM_PV' | 'CODIGO_CLIENTE'; readonly parametros: ParametrosNovaPeca; readonly permissoes: readonly string[] }) {
  const peloCadastro = window.location.pathname.startsWith('/cadastros/pecas/');
  const voltarHref = peloCadastro ? `/cadastros/pecas${parametros.clienteId ? `?clienteId=${encodeURIComponent(parametros.clienteId)}` : ''}` : '/pcp/roteiros/nova';
  const podeCriarMaterial = permissoes.includes(PERMISSAO_CRIAR_MATERIAL);
  const [pedido, setPedido] = useState<PedidoVendaView | null>(null);
  const [carregando, setCarregando] = useState(modo === 'ITEM_PV');
  const [erroCarregar, setErroCarregar] = useState<string | null>(null);
  const [servicos, setServicos] = useState<Awaited<ReturnType<typeof servicosComCelulas>>>([]);
  const [formulario, setFormulario] = useState<FormularioMaterial>({
    modo: 'NOVA', materialIdExistente: '', codigo: parametros.codigo ?? '', nome: parametros.descricao ?? '', unidade: 'un', cor: '',
  });
  const [existente, setExistente] = useState<MaterialLookupPv | null>(null);
  const [etapas, setEtapas] = useState<EtapaEdicao[] | null>(null);
  const [modeloId, setModeloId] = useState<string | null>(null);
  const [motivo, setMotivo] = useState(MOTIVO_PADRAO);
  const [inicioAberto, setInicioAberto] = useState(false);
  const [mostrarErros, setMostrarErros] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  const item: PedidoVendaItemView | null = pedido?.itens.find((candidato) => candidato.id === parametros.itemId) ?? null;

  useEffect(() => {
    let vivo = true;
    void servicosComCelulas().then((lista) => { if (vivo) setServicos(lista); }).catch(() => { if (vivo) setErro('Não foi possível carregar os serviços e células. Recarregue a página.'); });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    if (modo !== 'ITEM_PV' || !parametros.pedidoVendaId) return;
    let vivo = true;
    setCarregando(true);
    apiFetch<PedidoVendaView>(`/api/v1/vendas/pedidos/${encodeURIComponent(parametros.pedidoVendaId)}`)
      .then((carregado) => {
        if (!vivo) return;
        setPedido(carregado);
        const doItem = carregado.itens.find((candidato) => candidato.id === parametros.itemId);
        if (!doItem) { setErroCarregar('Item do pedido não encontrado.'); return; }
        const iniciais = valoresIniciaisDoItem(doItem);
        setFormulario((atual) => ({ ...atual, codigo: iniciais.codigo, nome: iniciais.nome, unidade: iniciais.unidade, cor: iniciais.cor }));
      })
      .catch(() => { if (vivo) setErroCarregar('Não foi possível carregar o pedido. Verifique a conexão e tente novamente.'); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [modo, parametros.pedidoVendaId, parametros.itemId]);

  // Sem cadastros:gerenciar a "Nova peça" fica desligada e o cadastro segue pelo material já existente.
  useEffect(() => {
    if (permissoes.length > 0 && !podeCriarMaterial) setFormulario((atual) => (atual.modo === 'NOVA' ? { ...atual, modo: 'EXISTENTE' } : atual));
  }, [permissoes, podeCriarMaterial]);

  const mensagensMaterial = validarFormularioMaterial(formulario);
  const mensagensEtapas = etapas === null ? [] : validarEtapas(etapas);
  const motivoValido = motivo.trim().length >= MOTIVO_MINIMO;
  const jaTemPeca = modo === 'ITEM_PV' && item !== null && item.materialId !== null;

  const comecarPor = (inicio: InicioDeRoteiro): void => { setEtapas(inicio.etapas); setModeloId(inicio.modeloId); setMostrarErros(false); };

  const cadastrar = async (): Promise<void> => {
    if (mensagensMaterial.length > 0 || mensagensEtapas.length > 0 || (etapas !== null && !motivoValido)) { setMostrarErros(true); return; }
    setEnviando(true);
    setErro(null);
    const material = materialParaEnviar(formulario);
    const roteiro = etapas === null ? null : { etapas: etapasParaSalvar(etapas), motivo: motivo.trim(), ...(modeloId !== null ? { modeloId } : {}) };
    try {
      let materialId: string;
      if (modo === 'ITEM_PV' && item && parametros.pedidoVendaId && parametros.itemId) {
        const resultado = await cadastrarPecaDoItem(parametros.pedidoVendaId, parametros.itemId, corpoCadastrarPecaDoItem(item.versao, material, roteiro));
        materialId = resultado.materialId;
        setSucesso(MENSAGEM_PECA_CADASTRADA);
      } else {
        const resultado = await cadastrarPecaDoCodigo(corpoCadastrarPecaDoCodigo(parametros, material, roteiro));
        materialId = resultado.materialId;
        setSucesso(parametros.pendenciaId && resultado.pendenciaResolvida ? MENSAGEM_PENDENCIA_RESOLVIDA : MENSAGEM_PECA_CADASTRADA_PELO_CODIGO);
      }
      window.setTimeout(() => window.location.assign(peloCadastro ? `/cadastros/pecas/${encodeURIComponent(materialId)}${parametros.clienteId ? `?clienteId=${encodeURIComponent(parametros.clienteId)}` : ''}` : linkDaFichaDaPeca(materialId)), ATRASO_NAVEGACAO_MS);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível cadastrar a peça. Tente novamente.');
      setEnviando(false);
    }
  };

  if (carregando) return <Pagina><div className="flex flex-col gap-4" aria-busy="true" aria-label="Carregando o item do pedido">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div></Pagina>;
  if (erroCarregar || (modo === 'ITEM_PV' && !item)) {
    return (
      <Pagina>
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-destructive">{erroCarregar ?? 'Item do pedido não encontrado.'}</p>
            <Button asChild type="button" variant="outline"><a href="/pcp/roteiros/nova">Ver produtos sem cadastro</a></Button>
          </CardContent>
        </Card>
      </Pagina>
    );
  }

  const titulo = modo === 'ITEM_PV'
    ? `Cadastrar peça do pedido ${pedido?.numero ?? ''} — item ${item?.numeroItem ?? ''}`
    : `Cadastrar peça do código ${parametros.codigo ?? ''}${parametros.clienteNome ? ` — ${parametros.clienteNome}` : ''}`;

  return (
    <Pagina>
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <a className="text-sm text-muted-foreground underline-offset-4 hover:underline" href={voltarHref}>{peloCadastro ? 'Peças / Produtos' : 'Produto sem cadastro'}</a>
            <h1 className="text-[28px] font-semibold leading-tight">{titulo}</h1>
          </div>
          {modo === 'ITEM_PV' && pedido && item ? (
            <dl className="grid gap-4 md:grid-cols-2 lg:grid-cols-5" aria-label="Item do pedido">
              {([['Cliente', pedido.clienteNome], ['Código do cliente', item.codigoItemCliente], ['Descrição', item.descricao], ['Cor', item.cor ?? '—'], ['Quantidade', `${item.quantidade} ${item.unidade}`]] as const).map(([rotulo, valor]) => (
                <div key={rotulo} className="flex flex-col gap-1"><dt className="text-xs text-muted-foreground">{rotulo}</dt><dd className="text-sm">{valor}</dd></div>
              ))}
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">Código do cliente: <span className="font-semibold">{parametros.codigo}</span>{parametros.pendenciaId ? ' · a pendência será resolvida ao cadastrar.' : ''}</p>
          )}
          {jaTemPeca ? <p role="status" className="text-sm font-semibold">Este item já tem peça cadastrada: <a className="underline" href={linkDaFichaDaPeca(item?.materialId ?? '')}>abrir a ficha da peça</a>.</p> : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider">Peça</h2>
          <div role="group" aria-label="Peça nova ou existente" className="flex flex-wrap gap-2">
            <Button type="button" variant={formulario.modo === 'EXISTENTE' ? 'default' : 'outline'} aria-pressed={formulario.modo === 'EXISTENTE'} onClick={() => setFormulario({ ...formulario, modo: 'EXISTENTE' })}>Já existe no cadastro</Button>
            <Button type="button" variant={formulario.modo === 'NOVA' ? 'default' : 'outline'} aria-pressed={formulario.modo === 'NOVA'} disabled={!podeCriarMaterial} onClick={() => setFormulario({ ...formulario, modo: 'NOVA' })}>Nova peça</Button>
          </div>
          {!podeCriarMaterial ? <p className="text-sm text-muted-foreground">{MENSAGEM_SEM_PERMISSAO_MATERIAL_NOVO}</p> : null}
          {faltaPermissaoDeVinculo(modo, parametros.pendenciaId, formulario.modo === 'EXISTENTE', permissoes) ? <p role="status" className="text-sm text-muted-foreground">{MENSAGEM_SEM_PERMISSAO_VINCULAR}</p> : null}
          {formulario.modo === 'EXISTENTE' ? (
            <div className="flex flex-col gap-1">
              <label htmlFor="nova-peca-existente" className="text-sm font-semibold">Material já cadastrado</label>
              <MaterialCombobox id="nova-peca-existente" value={existente} invalido={mostrarErros && formulario.materialIdExistente === ''} onChange={(material) => { setExistente(material); setFormulario({ ...formulario, materialIdExistente: material.id }); }} />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="flex flex-col gap-1">
                <label htmlFor="nova-peca-codigo" className="text-sm font-semibold">Código</label>
                <Input id="nova-peca-codigo" maxLength={60} value={formulario.codigo} onChange={(evento) => setFormulario({ ...formulario, codigo: evento.target.value })} />
              </div>
              <div className="flex flex-col gap-1 lg:col-span-2">
                <label htmlFor="nova-peca-nome" className="text-sm font-semibold">Nome</label>
                <Input id="nova-peca-nome" maxLength={200} value={formulario.nome} onChange={(evento) => setFormulario({ ...formulario, nome: evento.target.value })} />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="nova-peca-unidade" className="text-sm font-semibold">Unidade</label>
                <Input id="nova-peca-unidade" maxLength={10} value={formulario.unidade} onChange={(evento) => setFormulario({ ...formulario, unidade: evento.target.value })} />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="nova-peca-cor" className="text-sm font-semibold">Cor</label>
                <Input id="nova-peca-cor" maxLength={40} value={formulario.cor} onChange={(evento) => setFormulario({ ...formulario, cor: evento.target.value })} />
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">{NOTA_MATERIAL_DA_OMIE}</p>
          {mostrarErros && mensagensMaterial.length > 0 ? (
            <ul role="alert" className="flex flex-col gap-1 text-sm text-destructive">{mensagensMaterial.map((mensagem, indice) => <li key={`${indice}-${mensagem}`}>{mensagem}</li>)}</ul>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider">Roteiro</h2>
            <Button type="button" variant="outline" onClick={() => setInicioAberto(true)}>{etapas === null ? 'Definir roteiro' : 'Recomeçar a partir de…'}</Button>
          </div>
          {etapas === null ? (
            <p className="text-sm text-muted-foreground">Você pode cadastrar a peça agora e montar o roteiro depois, na ficha da peça. A OP só é gerada quando a peça tem roteiro.</p>
          ) : (
            <>
              <EditorEtapas etapas={etapas} servicos={servicos} onChange={setEtapas} mostrarErros={mostrarErros} />
              <div className="flex flex-col gap-1">
                <label htmlFor="nova-peca-motivo" className="text-sm font-semibold">Motivo</label>
                <Textarea id="nova-peca-motivo" value={motivo} maxLength={500} rows={2} onChange={(evento) => setMotivo(evento.target.value)} />
                <p className="text-xs text-muted-foreground">Mínimo de {MOTIVO_MINIMO} caracteres. Fica no histórico de versões com o seu nome.</p>
                {mostrarErros && !motivoValido ? <p role="alert" className="text-xs text-destructive">Informe o motivo (mínimo de {MOTIVO_MINIMO} caracteres).</p> : null}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {erro ? <p role="alert" className="text-sm text-destructive">{erro}</p> : null}
      {sucesso ? <p role="status" className="text-sm font-semibold">{sucesso}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="button" disabled={enviando || jaTemPeca} onClick={() => void cadastrar()}>{enviando ? 'Cadastrando...' : 'Cadastrar peça'}</Button>
        <Button asChild type="button" variant="outline"><a href={voltarHref}>Voltar</a></Button>
      </div>

      <IniciarRoteiroDialog aberto={inicioAberto} onAbertoChange={setInicioAberto} servicos={servicos} onEscolher={comecarPor} />
    </Pagina>
  );
}
